import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { Link as RouterLink } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import HistoryIcon from "@material-ui/icons/History";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SendIcon from "@material-ui/icons/Send";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import EmptyState from "../../components/EmptyState";
import { PageHeader, StatusBadge } from "../../components/ui";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { getTokens } from "../../styles/tokens";

import BillingRuleModal from "./BillingRuleModal";
import BillingRulePreviewModal from "./BillingRulePreviewModal";
import BillingRuleRunsDrawer from "./BillingRuleRunsDrawer";
import BillingRuleTestSendDialog from "./BillingRuleTestSendDialog";
import {
  ROWS_PER_PAGE,
  isWhatsappConnection,
  isOfficialChannel,
  formatHour,
  stageLabel,
  stageCode,
  getTotals,
  skippedBreakdownText,
  formatCount,
  ruleToBody,
} from "./helpers";
import useCampaignTotals from "./useCampaignTotals";

const COLUMNS = 9;

// Tom do selo por estágio: antes = informativo, no dia = atenção, depois = crítico.
const STAGE_TONE = { before: "info", due: "warning", after: "error" };

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // O cartão acompanha a altura do conteúdo; se passar da tela, quem rola é o miolo.
    tablePaper: {
      flex: "0 1 auto",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: t.shadow.card,
    },
    tableWrap: {
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      minWidth: 980,
    },
    nameCell: {
      fontWeight: 600,
      minWidth: 140,
      color: t.text,
    },
    inactiveName: {
      color: t.textSecondary,
      fontWeight: 500,
    },
    stageCell: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 3,
      whiteSpace: "nowrap",
    },
    stageCode: {
      fontFamily: "monospace",
      fontSize: 11,
      color: t.textMuted,
      paddingLeft: 2,
    },
    secondaryLine: {
      display: "block",
      fontSize: 12,
      color: t.textMuted,
    },
    connectionCell: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      whiteSpace: "nowrap",
    },
    officialIcon: {
      fontSize: 16,
      color: t.semantic.success,
    },
    templateCell: {
      maxWidth: 220,
    },
    templateText: {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: t.textSecondary,
      fontSize: 13,
    },
    templateShortcode: {
      fontFamily: "monospace",
      fontSize: 12,
      color: t.textSecondary,
    },
    // selos (PDF / Botões) numa linha própria abaixo do texto cortado com reticências
    chipRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
      marginTop: 4,
    },
    chipIcon: {
      fontSize: 13,
    },
    hourCell: {
      whiteSpace: "nowrap",
    },
    lastRun: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 2,
    },
    lastRunDate: {
      whiteSpace: "nowrap",
    },
    lastRunTotals: {
      fontSize: 12,
      color: t.textSecondary,
      maxWidth: 230,
    },
    actions: {
      whiteSpace: "nowrap",
    },
    menuIcon: {
      minWidth: 32,
      color: t.textSecondary,
    },
    menuDanger: {
      color: t.semantic.error,
      "& .MuiListItemIcon-root": { color: t.semantic.error },
    },
    emptyExample: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: 8,
      marginBottom: 16,
    },
    pagination: {
      flex: "none",
      borderTop: `1px solid ${t.line}`,
    },
  };
});

const BillingRules = () => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsapps, setWhatsapps] = useState([]);
  const [erpIntegrations, setErpIntegrations] = useState([]);
  const [erpLoaded, setErpLoaded] = useState(false);
  const [page, setPage] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [previewRule, setPreviewRule] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [runningId, setRunningId] = useState(null);
  // Rule selected for "send a test to a number"
  const [testRule, setTestRule] = useState(null);

  const [confirmRun, setConfirmRun] = useState(null);
  // Rule whose run-now returned 409 (already executed today) — asks for force
  const [confirmForce, setConfirmForce] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [runsRule, setRunsRule] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  // Menu "⋮" da linha: { anchor, rule }
  const [rowMenu, setRowMenu] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing-rules");
      if (isMountedRef.current) setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp");
        if (active) setWhatsapps((Array.isArray(data) ? data : []).filter(isWhatsappConnection));
      } catch (err) {
        toastError(err);
      }
      try {
        // Same source as InvoiceCampaigns: every SGP/IXC integration of the company
        const { data } = await api.get("/isp/erp-integrations");
        if (active) setErpIntegrations(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setErpLoaded(true);
      }
    })();
    fetchRules();
    return () => {
      active = false;
    };
  }, [fetchRules]);

  const openNew = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const handleToggleActive = async (rule, active) => {
    setTogglingId(rule.id);
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, active } : r)));
    try {
      await api.put(`/billing-rules/${rule.id}`, { ...ruleToBody(rule), active });
      toast.success(
        active
          ? i18n.t("billingRules.toasts.activated")
          : i18n.t("billingRules.toasts.deactivated")
      );
    } catch (err) {
      toastError(err);
      if (isMountedRef.current) {
        setRules((prev) =>
          prev.map((r) => (r.id === rule.id ? { ...r, active: !active } : r))
        );
      }
    } finally {
      if (isMountedRef.current) setTogglingId(null);
    }
  };

  const handlePreview = async (rule) => {
    setPreviewRule(rule);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/billing-rules/${rule.id}/preview`);
      if (isMountedRef.current) setPreview(data || { count: 0, samples: [], warnings: [] });
    } catch (err) {
      toastError(err);
      if (isMountedRef.current) setPreviewRule(null);
    } finally {
      if (isMountedRef.current) setPreviewLoading(false);
    }
  };

  const runNow = async (rule, force = false) => {
    if (!rule) return;
    setRunningId(rule.id);
    try {
      const { data } = await api.post(
        `/billing-rules/${rule.id}/run-now`,
        force ? { force: true } : {}
      );
      const totals = getTotals(data?.totals);
      if (data?.status === "empty" || totals.total === 0) {
        toast.info(
          i18n.t("billingRules.toasts.runEmpty", {
            candidates: totals.candidates,
            skipped: totals.skipped,
          })
        );
      } else {
        toast.success(
          i18n.t("billingRules.toasts.runStarted", {
            total: totals.total,
            pending: totals.pending,
            skipped: totals.skipped,
            campaignId: data?.invoiceCampaignId || "-",
          })
        );
      }
      if (!isMountedRef.current) return;
      setPreviewRule(null);
      setPreview(null);
      fetchRules();
    } catch (err) {
      if (err?.response?.status === 409 && !force) {
        // Already executed today: ask before re-running with { force: true }
        if (isMountedRef.current) setConfirmForce(rule);
      } else {
        toastError(err);
      }
    } finally {
      if (isMountedRef.current) setRunningId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/billing-rules/${confirmDelete.id}`);
      toast.success(i18n.t("billingRules.toasts.deleted"));
      fetchRules();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmDelete(null);
    }
  };

  const closeRowMenu = () => setRowMenu(null);
  // Fecha o menu e dispara a mesma ação que o ícone solto disparava.
  const fromMenu = (action) => () => {
    const rule = rowMenu?.rule;
    setRowMenu(null);
    if (rule) action(rule);
  };

  // Selo "PDF" quando a regra anexa o boleto em PDF (só no canal não oficial)
  const renderPdfChip = (rule) =>
    rule.sendPdf && !isOfficialChannel(rule.whatsapp) ? (
      <Tooltip title={i18n.t("billingRules.table.sendPdf")}>
        <span>
          <StatusBadge
            color="neutral"
            size="sm"
            icon={<PictureAsPdfIcon className={classes.chipIcon} />}
            label="PDF"
            aria-label={i18n.t("billingRules.table.sendPdf")}
          />
        </span>
      </Tooltip>
    ) : null;

  // Selo quando a regra manda botões de copiar (PIX / código de barras / abrir boleto)
  const renderCopyChip = (rule) =>
    rule.copyButtons ? (
      <Tooltip title={i18n.t("billingRules.table.copyButtons")}>
        <span>
          <StatusBadge
            color="neutral"
            size="sm"
            icon={<FileCopyIcon className={classes.chipIcon} />}
            label={i18n.t("billingRules.table.copyButtonsShort")}
            aria-label={i18n.t("billingRules.table.copyButtons")}
          />
        </span>
      </Tooltip>
    ) : null;

  const renderChips = (rule) => {
    const pdf = renderPdfChip(rule);
    const copy = renderCopyChip(rule);
    if (!pdf && !copy) return null;
    return (
      <div className={classes.chipRow}>
        {pdf}
        {copy}
      </div>
    );
  };

  const renderTemplate = (rule) => {
    if (rule.quickMessage) {
      return (
        <Tooltip title={i18n.t("billingRules.table.metaTemplate")}>
          <span className={classes.templateShortcode}>{rule.quickMessage.shortcode}</span>
        </Tooltip>
      );
    }
    if (rule.textTemplate) {
      return (
        <>
          <Tooltip title={rule.textTemplate}>
            <span className={classes.templateText}>{rule.textTemplate}</span>
          </Tooltip>
          {renderChips(rule)}
        </>
      );
    }
    return renderChips(rule) || "-";
  };

  const renderLastRun = (rule) => {
    const run = rule.lastRun;
    if (!run) return "-";
    const campaign = run.invoiceCampaignId ? campaignsById[String(run.invoiceCampaignId)] : null;
    const totals = getTotals(run.totals, campaign);
    const skippedTip = skippedBreakdownText(totals.skippedBreakdown);
    return (
      <div className={classes.lastRun}>
        <span className={classes.lastRunDate}>
          {run.runDate ? datetimeToClient(run.runDate) : "-"}
        </span>
        <span className={classes.lastRunTotals}>
          {i18n.t("billingRules.totals.total")} {totals.total} ·{" "}
          {i18n.t("billingRules.totals.sent")} {formatCount(totals.sent)} ·{" "}
          {i18n.t("billingRules.totals.failed")} {formatCount(totals.failed)} ·{" "}
          <Tooltip title={skippedTip} disableHoverListener={!skippedTip}>
            <span>
              {i18n.t("billingRules.totals.skipped")} {totals.skipped}
            </span>
          </Tooltip>
        </span>
        {run.invoiceCampaignId && (
          <Link component={RouterLink} to="/invoice-campaigns" style={{ fontSize: 12 }}>
            {i18n.t("billingRules.table.viewCampaign")} #{run.invoiceCampaignId}
          </Link>
        )}
      </div>
    );
  };

  const noErp = erpLoaded && erpIntegrations.length === 0;
  const pagedRules = rules.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  // sent/failed of the last run live on the campaign — fetched lazily for visible rows
  const campaignsById = useCampaignTotals(
    pagedRules.map((r) => r.lastRun?.invoiceCampaignId).filter(Boolean)
  );

  const renderEmptyState = () => {
    if (noErp) {
      return (
        <EmptyState
          icon={<LinkOffIcon />}
          title={i18n.t("billingRules.empty.noErpTitle")}
          message={i18n.t("billingRules.empty.noErpText")}
          action={
            <Button component={RouterLink} to="/queue-integration" variant="outlined">
              {i18n.t("billingRules.empty.noErpAction")}
            </Button>
          }
        />
      );
    }
    return (
      <EmptyState
        icon={<ReceiptOutlinedIcon />}
        title={i18n.t("billingRules.empty.noRulesTitle")}
        message={i18n.t("billingRules.empty.noRulesText")}
        action={
          <>
            <div className={classes.emptyExample}>
              <StatusBadge
                color={STAGE_TONE.before}
                label={`D-3 · ${i18n.t("billingRules.empty.exampleBefore")}`}
              />
              <StatusBadge
                color={STAGE_TONE.due}
                label={`D0 · ${i18n.t("billingRules.empty.exampleDue")}`}
              />
              <StatusBadge
                color={STAGE_TONE.after}
                label={`D+5 · ${i18n.t("billingRules.empty.exampleAfter")}`}
              />
            </div>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
              {i18n.t("billingRules.buttons.new")}
            </Button>
          </>
        }
      />
    );
  };

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={
          confirmRun &&
          `${i18n.t("billingRules.confirm.runTitle")} ${confirmRun.name}?`
        }
        open={!!confirmRun}
        onClose={() => setConfirmRun(null)}
        onConfirm={() => runNow(confirmRun)}
      >
        {i18n.t("billingRules.confirm.runMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          confirmForce &&
          `${i18n.t("billingRules.confirm.forceTitle")} ${confirmForce.name}?`
        }
        open={!!confirmForce}
        onClose={() => setConfirmForce(null)}
        onConfirm={() => runNow(confirmForce, true)}
      >
        {i18n.t("billingRules.confirm.forceMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          confirmDelete &&
          `${i18n.t("billingRules.confirm.deleteTitle")} ${confirmDelete.name}?`
        }
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      >
        {i18n.t("billingRules.confirm.deleteMessage")}
      </ConfirmationModal>

      {modalOpen && (
        <BillingRuleModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingRule(null);
          }}
          rule={editingRule}
          erpIntegrations={erpIntegrations}
          whatsapps={whatsapps}
          onSaved={fetchRules}
        />
      )}

      <BillingRulePreviewModal
        open={!!previewRule}
        onClose={() => {
          setPreviewRule(null);
          setPreview(null);
        }}
        rule={previewRule}
        preview={preview}
        loading={previewLoading}
        running={!!previewRule && runningId === previewRule.id}
        onRunNow={() => runNow(previewRule)}
      />

      <BillingRuleTestSendDialog
        open={!!testRule}
        rule={testRule}
        onClose={() => setTestRule(null)}
      />
      <BillingRuleRunsDrawer
        open={!!runsRule}
        onClose={() => setRunsRule(null)}
        rule={runsRule}
      />

      <Menu
        anchorEl={rowMenu?.anchor || null}
        open={!!rowMenu}
        onClose={closeRowMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={fromMenu(handlePreview)}>
          <ListItemIcon className={classes.menuIcon}>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("billingRules.buttons.preview")}
        </MenuItem>
        <MenuItem onClick={fromMenu(setTestRule)}>
          <ListItemIcon className={classes.menuIcon}>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("billingRules.buttons.testSend")}
        </MenuItem>
        <MenuItem
          onClick={fromMenu(setConfirmRun)}
          disabled={!!rowMenu && runningId === rowMenu.rule.id}
        >
          <ListItemIcon className={classes.menuIcon}>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("billingRules.buttons.runNow")}
        </MenuItem>
        <MenuItem onClick={fromMenu(setRunsRule)}>
          <ListItemIcon className={classes.menuIcon}>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("billingRules.buttons.history")}
        </MenuItem>
        <Divider component="li" />
        <MenuItem onClick={fromMenu(setConfirmDelete)} className={classes.menuDanger}>
          <ListItemIcon className={classes.menuIcon}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("billingRules.buttons.delete")}
        </MenuItem>
      </Menu>

      <PageHeader
        title={i18n.t("billingRules.title")}
        subtitle="Lembretes de fatura por WhatsApp antes, no dia e depois do vencimento."
        badge={
          rules.length > 0 ? (
            <StatusBadge color="neutral" dot={false} label={String(rules.length)} />
          ) : null
        }
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openNew}
            disabled={noErp}
          >
            {i18n.t("billingRules.buttons.new")}
          </Button>
        }
      />

      <Paper className={classes.tablePaper} variant="outlined">
        {/* vazio fora da tabela: dentro dela o aviso ficaria centrado na largura mínima, fora da tela no celular */}
        {!loading && rules.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className={classes.tableWrap}>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("billingRules.table.name")}</TableCell>
                  <TableCell>{i18n.t("billingRules.table.stage")}</TableCell>
                  <TableCell>{i18n.t("billingRules.table.erp")}</TableCell>
                  <TableCell>{i18n.t("billingRules.table.connection")}</TableCell>
                  <TableCell>{i18n.t("billingRules.table.template")}</TableCell>
                  <TableCell>{i18n.t("billingRules.table.sendHour")}</TableCell>
                  <TableCell>{i18n.t("billingRules.table.active")}</TableCell>
                  <TableCell>{i18n.t("billingRules.table.lastRun")}</TableCell>
                  <TableCell align="right">{i18n.t("billingRules.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell
                      className={`${classes.nameCell} ${rule.active ? "" : classes.inactiveName}`}
                    >
                      {rule.name}
                    </TableCell>
                    <TableCell>
                      <div className={classes.stageCell}>
                        <StatusBadge
                          color={STAGE_TONE[rule.stage] || STAGE_TONE.due}
                          label={stageLabel(rule.stage, rule.offsetDays)}
                        />
                        <span className={classes.stageCode}>
                          {stageCode(rule.stage, rule.offsetDays)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {rule.erpIntegration ? (
                        <>
                          {rule.erpIntegration.name}
                          {rule.erpIntegration.type && (
                            <span className={classes.secondaryLine}>{rule.erpIntegration.type}</span>
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {rule.whatsapp ? (
                        <span className={classes.connectionCell}>
                          {isOfficialChannel(rule.whatsapp) && (
                            <Tooltip title={i18n.t("billingRules.table.officialConnection")}>
                              <VerifiedUserIcon className={classes.officialIcon} />
                            </Tooltip>
                          )}
                          {rule.whatsapp.name}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className={classes.templateCell}>{renderTemplate(rule)}</TableCell>
                    <TableCell className={classes.hourCell}>{formatHour(rule.sendHour)}</TableCell>
                    <TableCell>
                      <Switch
                        color="primary"
                        size="small"
                        checked={!!rule.active}
                        disabled={togglingId === rule.id}
                        onChange={(e) => handleToggleActive(rule, e.target.checked)}
                        inputProps={{ "aria-label": i18n.t("billingRules.table.active") }}
                      />
                    </TableCell>
                    <TableCell>{renderLastRun(rule)}</TableCell>
                    <TableCell align="right" className={classes.actions}>
                      <Tooltip title={i18n.t("billingRules.buttons.edit")}>
                        <IconButton
                          size="small"
                          aria-label={i18n.t("billingRules.buttons.edit")}
                          onClick={() => openEdit(rule)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mais ações">
                        <IconButton
                          size="small"
                          aria-label="Mais ações"
                          aria-haspopup="true"
                          onClick={(e) => setRowMenu({ anchor: e.currentTarget, rule })}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={COLUMNS} />}
              </TableBody>
            </Table>
          </div>
        )}
        {rules.length > ROWS_PER_PAGE && (
          <TablePagination
            component="div"
            className={classes.pagination}
            count={rules.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[]}
          />
        )}
      </Paper>
    </MainContainer>
  );
};

export default BillingRules;
